/**
 * [PHASE 0.2 - NOV 11, 2025]: Concentration System Service
 * [PURPOSE]: Manages spell concentration per D&D 5e rules
 * [WHY]: Core mechanic was missing - prevents game-breaking multiple concentration spells
 * [INTEGRATION]: SpellsPanel (spell casting), VitalStatsCard (damage), ConditionsTracker (unconscious/death)
 *
 * D&D 5e Concentration Rules (PHB p.203-204):
 * 1. Only ONE concentration spell at a time
 * 2. Casting another concentration spell ends the first
 * 3. Taking damage requires a Constitution saving throw
 * 4. DC = 10 or half damage taken (whichever is higher)
 * 5. Concentration ends on:
 *    - Casting another concentration spell
 *    - Taking damage and failing the save
 *    - Being incapacitated or killed
 *    - Player chooses to end it
 *
 * Created: November 11, 2025
 * Author: Claude (God-Tier Programmer Mode)
 */

import { systemBus, EVENTS } from './systemBus';
import { effectCalculator } from './effectCalculator';
import effectRegistry from './effectRegistry';

/**
 * [PURPOSE]: Concentration System - singleton service
 * [WHY]: Maintains spell concentration state across the application
 * [INTEGRATION]: All spell-related components use this single instance
 */
class ConcentrationSystem {
  constructor() {
    /**
     * Active concentration spell data
     * @type {Object|null}
     * @property {string} spellName - Name of the spell being concentrated on
     * @property {number} spellLevel - Level at which spell was cast
     * @property {number} startedAt - Timestamp when concentration started
     * @property {number} baseDC - Base DC for concentration checks (10)
     * @property {string} spellId - Unique identifier for the spell
     * @property {string} characterName - Name of character concentrating
     */
    this.activeConcentration = null;
  }

  /**
   * Check if character is currently concentrating on a spell
   * @returns {boolean} True if concentrating
   */
  isConcentrating() {
    return this.activeConcentration !== null;
  }

  /**
   * Get details of the active concentration spell
   * @returns {Object|null} Concentration data or null
   */
  getActiveConcentration() {
    return this.activeConcentration;
  }

  /**
   * [Phase 4b CW-parity (C) — APR 2026]: Attach a sourceId-prefix that the
   * concentration system should retract via `effectRegistry.removeByPrefix`
   * when concentration ends. Called by `spellCastingService.castSpell` right
   * after it registers a concentration spell's `spellEffects` block. Without
   * this hook, effects would leak into the registry forever.
   *
   * @param {string} sourceIdPrefix - the same prefix used to register effects
   */
  attachEffectsSourceId(sourceIdPrefix) {
    if (!this.activeConcentration) return;
    if (!sourceIdPrefix) return;
    this.activeConcentration.effectsSourceId = sourceIdPrefix;
  }

  /**
   * Start concentrating on a new spell
   * [WHY]: D&D 5e allows only one concentration spell at a time
   *
   * @param {Object} spell - Spell object from SRD
   * @param {Object} character - Character object
   * @param {Function} [showStatus] - Optional status message function
   * @param {Function} [logActivity] - Optional activity logging function
   * @returns {Object} Result object {success, previousSpell}
   */
  startConcentration(spell, character, showStatus, logActivity) {
    // [VALIDATION]: Check if spell requires concentration
    if (!spell.concentration) {
      console.warn('[ConcentrationSystem] Attempted to start concentration on non-concentration spell:', spell.name);
      return {
        success: false,
        reason: 'spell_not_concentration'
      };
    }

    // [STEP 1]: End previous concentration if exists
    let previousSpell = null;
    if (this.activeConcentration) {
      previousSpell = this.activeConcentration.spellName;

      // Show status message about losing concentration
      if (showStatus) {
        showStatus(`⚠️ Lost concentration on ${previousSpell}`, 3000);
      }

      // Log activity
      if (logActivity) {
        logActivity(
          `Lost concentration on ${previousSpell} (new spell cast: ${spell.name})`,
          'spell',
          {
            type: 'concentration_lost',
            previousSpell,
            newSpell: spell.name,
            reason: 'new_spell_cast'
          }
        );
      }

      // Break previous concentration
      this.breakConcentration('new_spell_cast', showStatus, logActivity);
    }

    // [STEP 2]: Set new active concentration
    this.activeConcentration = {
      spellName: spell.name,
      spellLevel: spell.level || 0,
      startedAt: Date.now(),
      baseDC: 10, // D&D 5e base concentration DC
      spellId: `${spell.name}_${Date.now()}`,
      characterName: character.name || 'Unknown',
      // [Phase 4b CW-parity (C) — APR 2026]: Set by attachEffectsSourceId() if
      //   the cast registered any spellEffects. Cleared on breakConcentration
      //   so the registered effects are retracted when concentration ends.
      effectsSourceId: null,
    };

    // [STEP 3]: Emit CONCENTRATION_STARTED event
    systemBus.emit(EVENTS.CONCENTRATION_STARTED, {
      spell: spell.name,
      spellLevel: spell.level,
      character: character.name || 'Unknown',
      timestamp: Date.now(),
      spellId: this.activeConcentration.spellId
    });

    // [STEP 4]: Show success message
    if (showStatus) {
      showStatus(`✨ Concentrating on ${spell.name}`, 2000);
    }

    // [STEP 5]: Log activity
    if (logActivity) {
      logActivity(
        `Started concentrating on ${spell.name}`,
        'spell',
        {
          type: 'concentration_started',
          spell: spell.name,
          spellLevel: spell.level,
          previousSpell
        }
      );
    }

    return {
      success: true,
      previousSpell
    };
  }

  /**
   * Check concentration when taking damage (D&D 5e PHB p.203)
   * [WHY]: D&D 5e requires Constitution save when concentrating character takes damage
   * [DC]: 10 or half damage taken (whichever is higher)
   *
   * @param {number} damageAmount - Amount of damage taken
   * @param {Object} character - Character object
   * @param {Object} inventory - Inventory array for magic item bonuses
   * @param {Array} activeQuickBonuses - Active quick bonuses
   * @param {Function} [showStatus] - Optional status message function
   * @param {Function} [logActivity] - Optional activity logging function
   * @returns {Object} Result {success, save, dc, maintained}
   */
  checkConcentration(damageAmount, character, inventory, activeQuickBonuses, showStatus, logActivity) {
    // [VALIDATION]: Check if concentrating
    if (!this.activeConcentration) {
      return {
        success: true,
        maintained: true,
        reason: 'not_concentrating'
      };
    }

    // [STEP 1]: Calculate DC (10 or half damage, whichever is higher)
    const halfDamage = Math.floor(damageAmount / 2);
    const dc = Math.max(10, halfDamage);

    // [STEP 2]: Roll Constitution saving throw
    const conSave = this.rollConcentrationSave(character, inventory, activeQuickBonuses, dc);

    // [STEP 3]: Check if save succeeded
    const maintained = conSave >= dc;

    // [STEP 4]: Log the check
    const spellName = this.activeConcentration.spellName;
    if (logActivity) {
      logActivity(
        maintained
          ? `✓ Maintained concentration on ${spellName} (${conSave} vs DC ${dc})`
          : `✗ Lost concentration on ${spellName} (${conSave} vs DC ${dc})`,
        'dice',
        {
          type: 'concentration_check',
          spell: spellName,
          damage: damageAmount,
          dc,
          save: conSave,
          maintained
        }
      );
    }

    // [STEP 5]: Handle result
    if (maintained) {
      // Success - maintained concentration
      if (showStatus) {
        showStatus(`✓ Maintained concentration on ${spellName} (${conSave} vs DC ${dc})`, 3000);
      }

      // Emit event
      systemBus.emit(EVENTS.CONCENTRATION_CHECK_REQUESTED, {
        spell: spellName,
        damage: damageAmount,
        dc,
        save: conSave,
        result: 'maintained'
      });

      return {
        success: true,
        maintained: true,
        save: conSave,
        dc
      };
    } else {
      // Failed - lost concentration
      if (showStatus) {
        showStatus(`✗ Lost concentration on ${spellName} (${conSave} vs DC ${dc})`, 3000);
      }

      // Break concentration
      this.breakConcentration('failed_save', showStatus, logActivity, { damage: damageAmount, dc, save: conSave });

      return {
        success: true,
        maintained: false,
        save: conSave,
        dc
      };
    }
  }

  /**
   * Roll a concentration saving throw (Constitution save)
   * [WHY]: D&D 5e uses Constitution for concentration checks
   *
   * @param {Object} character - Character object
   * @param {Object} inventory - Inventory array for magic item bonuses
   * @param {Array} activeQuickBonuses - Active quick bonuses
   * @param {number} dc - DC for the save (for logging)
   * @returns {number} Total save result
   */
  rollConcentrationSave(character, inventory, activeQuickBonuses, dc) {
    // [STEP 1]: Roll d20
    const d20Roll = Math.floor(Math.random() * 20) + 1;

    // [STEP 2]: Get Constitution modifier using effectCalculator
    // [WHY]: Includes magic items (Amulet of Health), feats, temp effects
    const conMod = effectCalculator.getEffectiveModifier(
      character,
      inventory,
      activeQuickBonuses,
      'constitution'
    );

    // [FIX APR 2026 — F-18]: Use effectCalculator for proficiency bonus
    // [WHY]: Manual if/else chain didn't account for effectRegistry modifiers
    let profBonus = 0;
    if (character.proficiencies?.saves?.includes('constitution')) {
      profBonus = effectCalculator.getProficiencyBonus(character);
    }

    // [FIX APR 2026 — F-13]: Data-driven concentration advantage check
    // [WHY]: Custom feats with concentrationAdvantage effect now work (not just hardcoded War Caster)
    // [ESR-009 FIX — MAY 2026]: Pre-fix used getEffectiveValue(stat, false, character)
    //   which works "by accident" — JS coerces `false + 1` to `1`, so the check
    //   passes for War Caster's value:1 effect. But if a future CW author
    //   registers value:'cha_modifier' (string formula), parseInt=0 and the
    //   check silently fails. Now uses getEffectsForStat which is the
    //   contract-correct API for "is there any effect of this type?" boolean
    //   checks. Reserves getEffectiveValue for numeric stats per its docstring.
    let advantage = false;
    const hasConcentrationAdvantage = effectRegistry.getEffectsForStat('concentrationAdvantage', character).length > 0;
    if (hasConcentrationAdvantage) {
      advantage = true;
      // Roll second d20 for advantage
      const d20Roll2 = Math.floor(Math.random() * 20) + 1;
      const betterRoll = Math.max(d20Roll, d20Roll2);

      return betterRoll + conMod + profBonus;
    }

    // [STEP 5]: Calculate total
    const total = d20Roll + conMod + profBonus;


    return total;
  }

  /**
   * Break concentration (spell ends)
   * [WHY]: D&D 5e concentration ends when broken
   *
   * @param {string} reason - Reason for breaking (failed_save, new_spell_cast, incapacitated, manual, etc.)
   * @param {Function} [showStatus] - Optional status message function
   * @param {Function} [logActivity] - Optional activity logging function
   * @param {Object} [metadata] - Additional metadata for logging
   */
  breakConcentration(reason, showStatus, logActivity, metadata = {}) {
    // [VALIDATION]: Check if concentrating
    if (!this.activeConcentration) {
      console.warn('[ConcentrationSystem] Attempted to break concentration when not concentrating');
      return;
    }

    const spellName = this.activeConcentration.spellName;
    const duration = Date.now() - this.activeConcentration.startedAt;
    const durationSeconds = Math.floor(duration / 1000);
    const durationMinutes = Math.floor(durationSeconds / 60);

    // [STEP 1]: Emit CONCENTRATION_BROKEN event
    systemBus.emit(EVENTS.CONCENTRATION_BROKEN, {
      spell: spellName,
      reason,
      duration: durationSeconds,
      characterName: this.activeConcentration.characterName,
      timestamp: Date.now(),
      ...metadata
    });

    // [Phase 4b CW-parity (C) — APR 2026]: Retract any spellEffects that the
    //   cast registered into effectRegistry. Without this, buff spells like
    //   Bless would leak forever after the concentration ended.
    const sourceIdPrefix = this.activeConcentration.effectsSourceId;
    if (sourceIdPrefix) {
      try {
        effectRegistry.removeByPrefix(sourceIdPrefix);
        effectCalculator.clearCache();
      } catch (err) {
        console.warn('[ConcentrationSystem] failed to retract spell effects:', err);
      }
    }

    // [STEP 2]: Clear active concentration
    this.activeConcentration = null;

    // [STEP 3]: Log activity (if not already logged by caller)
    if (logActivity && reason !== 'new_spell_cast') {
      const reasonText = {
        'failed_save': 'failed concentration save',
        'incapacitated': 'became incapacitated',
        'unconscious': 'fell unconscious',
        'death': 'died',
        'manual': 'manually ended concentration'
      }[reason] || reason;

      logActivity(
        `Concentration on ${spellName} ended (${reasonText}) after ${durationMinutes > 0 ? durationMinutes + ' min' : durationSeconds + ' sec'}`,
        'spell',
        {
          type: 'concentration_ended',
          spell: spellName,
          reason,
          duration: durationSeconds,
          ...metadata
        }
      );
    }

  }

  /**
   * Manually end concentration (player choice)
   * [WHY]: Players can voluntarily end concentration at any time
   *
   * @param {Function} [showStatus] - Optional status message function
   * @param {Function} [logActivity] - Optional activity logging function
   * @returns {boolean} True if concentration was ended
   */
  endConcentration(showStatus, logActivity) {
    if (!this.activeConcentration) {
      if (showStatus) {
        showStatus('Not currently concentrating on any spell', 2000);
      }
      return false;
    }

    const spellName = this.activeConcentration.spellName;

    if (showStatus) {
      showStatus(`Ended concentration on ${spellName}`, 2000);
    }

    this.breakConcentration('manual', showStatus, logActivity);

    return true;
  }

  /**
   * Check for conditions that break concentration automatically
   * [WHY]: D&D 5e - concentration ends on incapacitated, unconscious, death
   *
   * @param {Object} character - Character object with conditions
   * @param {Function} [showStatus] - Optional status message function
   * @param {Function} [logActivity] - Optional activity logging function
   * @returns {boolean} True if concentration was broken
   */
  checkConditions(character, showStatus, logActivity) {
    if (!this.activeConcentration) return false;

    const conditions = character.conditions || {};

    // Check for concentration-breaking conditions
    if (conditions.unconscious || character.currentHp <= 0) {
      this.breakConcentration('unconscious', showStatus, logActivity);
      return true;
    }

    if (conditions.incapacitated || conditions.paralyzed || conditions.stunned || conditions.petrified) {
      this.breakConcentration('incapacitated', showStatus, logActivity);
      return true;
    }

    return false;
  }
}

// [TDZ BUG FIX - NOV 17, 2025]: Changed from `const` to `var` to fix production minifier TDZ error
// [WHY]: Module-level `const` with instantiation can cause TDZ in minified bundles
// [CAUSE]: Webpack/minifier may reorder code, `var` is hoisted and has no TDZ
// [EXPORT]: Single instance (singleton pattern)
// [WHY]: Only one concentration state needed across entire application
export var concentrationSystem = new ConcentrationSystem();
